import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Copy, LayoutTemplate, Pencil, Plus, Trash2 } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Dialog,
  EmptyState,
  Input,
  Select,
  useToast,
} from '@solara/ui-shared';
import { useTemplates, useCreateTemplate, useDeleteTemplate } from '@solara/api-client/react';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

interface TemplateForm {
  templateId: string;
  name: string;
  description: string;
  buyerName: string;
  buyerTrn: string;
  vatRate: 5 | 0 | 'EXEMPT';
  itemDescription: string;
  unitPrice: number;
  category: string;
}

export default function TemplatesPage() {
  const { t } = useTranslation('templates');
  const { locale } = useI18n();
  const { toast } = useToast();
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });

  const templatesQuery = useTemplates();
  const createTpl = useCreateTemplate();
  const deleteTpl = useDeleteTemplate();

  const list: TemplateForm[] = (templatesQuery.data ?? []).map((row) => {
    // Backend payload (per services/core seed): { lines: [{ unitPriceMinor, quantity, description }], buyerName, defaultVatRate, currency }.
    // Older shape (legacy mock) put fields flat on payload. Support both.
    const p = (row.payload as Partial<TemplateForm> &
      Partial<{
        defaultVatRate: number;
        lines: Array<{ unitPriceMinor?: number; description?: string; quantity?: number }>;
      }> | null | undefined) ?? {};
    const firstLine = p.lines?.[0];
    const unitPriceFromMinor = firstLine?.unitPriceMinor != null ? firstLine.unitPriceMinor / 100 : undefined;
    return {
      templateId: row.id,
      name: row.name,
      description: p.description ?? '',
      buyerName: p.buyerName ?? '',
      buyerTrn: p.buyerTrn ?? '',
      vatRate: (p.vatRate ?? p.defaultVatRate ?? 5) as TemplateForm['vatRate'],
      itemDescription: p.itemDescription ?? firstLine?.description ?? '',
      unitPrice: p.unitPrice ?? unitPriceFromMinor ?? 0,
      category: p.category ?? '',
    };
  });

  const [editing, setEditing] = useState<TemplateForm | null>(null);

  function copyToNew() {
    toast({
      title: t('toast.applied'),
      description: t('toast.appliedBody'),
      tone: 'success',
    });
  }

  async function remove(id: string) {
    try {
      await deleteTpl.mutateAsync(id);
      toast({ title: t('toast.removed'), tone: 'warning' });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Delete failed', tone: 'danger' });
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
          <p className="text-fg-muted text-sm">{t('subtitle')}</p>
        </div>
        <Button
          onClick={() =>
            setEditing({
              templateId: 'new',
              name: '',
              description: '',
              buyerName: '',
              buyerTrn: '',
              vatRate: 5,
              itemDescription: '',
              unitPrice: 0,
              category: '',
            })
          }
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          {t('newTemplate')}
        </Button>
      </header>

      {templatesQuery.isLoading ? (
        <LoadingBlock />
      ) : templatesQuery.error ? (
        <ErrorBanner error={templatesQuery.error} />
      ) : list.length === 0 ? (
        <EmptyState icon={<LayoutTemplate className="h-6 w-6" />} title={t('empty')} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {list.map((tt) => (
            <Card key={tt.templateId} className="rounded-2xl">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <CardTitle>{tt.name}</CardTitle>
                  <Badge tone="brand">{tt.category}</Badge>
                </div>
              </CardHeader>
              <CardBody className="space-y-2 text-sm">
                <p className="text-fg-muted">{tt.description}</p>
                <div className="text-xs space-y-1 text-fg-subtle">
                  <div>{t('buyer')}: <span className="text-fg">{tt.buyerName}</span></div>
                  <div>{t('defaultItem')}: <span className="text-fg">{tt.itemDescription}</span></div>
                  <div>{t('unitPrice')}: <span className="text-fg num">{aedFmt.format(tt.unitPrice)}</span> · {t('vat')} {tt.vatRate === 'EXEMPT' ? t('exempt') : `${tt.vatRate}%`}</div>
                </div>
                <div className="flex items-center gap-2 pt-1 flex-wrap">
                  <Button asChild variant="primary" size="sm" className="gap-1.5">
                    <Link to="/new" onClick={copyToNew}>
                      {t('actions.apply')}
                      <ArrowRight className="h-3.5 w-3.5 rtl-mirror" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={copyToNew}>
                    <Copy className="h-3.5 w-3.5" />
                    {t('actions.duplicate')}
                  </Button>
                  <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setEditing(tt)}>
                    <Pencil className="h-3.5 w-3.5" />
                    {t('actions.edit')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 text-danger hover:bg-danger/5"
                    onClick={() => remove(tt.templateId)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {t('actions.remove')}
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={!!editing}
        onOpenChange={(o) => {
          if (!o) setEditing(null);
        }}
        title={editing?.templateId === 'new' ? t('dialog.newTitle') : t('dialog.editTitle', { name: editing?.name ?? '' })}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>{t('dialog.cancel')}</Button>
            <Button
              onClick={async () => {
                if (!editing) return;
                try {
                  const { templateId: _tid, name, ...payload } = editing;
                  void _tid;
                  await createTpl.mutateAsync({ name, payload });
                  toast({ title: t('toast.saved'), tone: 'success' });
                  setEditing(null);
                } catch (err) {
                  toast({
                    title: err instanceof Error ? err.message : 'Save failed',
                    tone: 'danger',
                  });
                }
              }}
            >
              {t('dialog.save')}
            </Button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-3">
            <Input label={t('dialog.name')} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <Input label={t('dialog.category')} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
            <Input label={t('dialog.description')} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label={t('dialog.buyerName')} value={editing.buyerName} onChange={(e) => setEditing({ ...editing, buyerName: e.target.value })} />
              <Input label={t('dialog.buyerTrn')} value={editing.buyerTrn} onChange={(e) => setEditing({ ...editing, buyerTrn: e.target.value })} inputMode="numeric" className="num" dir="ltr" />
              <Input label={t('dialog.defaultItem')} value={editing.itemDescription} onChange={(e) => setEditing({ ...editing, itemDescription: e.target.value })} containerClassName="sm:col-span-2" />
              <Input label={t('dialog.unitPrice')} value={String(editing.unitPrice)} onChange={(e) => setEditing({ ...editing, unitPrice: parseFloat(e.target.value || '0') })} inputMode="decimal" className="num text-end" dir="ltr" />
              <Select
                label={t('dialog.vatRate')}
                value={String(editing.vatRate)}
                onChange={(e) => {
                  const v = e.target.value;
                  setEditing({ ...editing, vatRate: v === 'EXEMPT' ? 'EXEMPT' : (Number(v) as 5 | 0) });
                }}
                options={[
                  { value: '5', label: '5%' },
                  { value: '0', label: '0%' },
                  { value: 'EXEMPT', label: t('exempt') },
                ]}
              />
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
